import type { BetterAuthClientPlugin } from "better-auth/client";
import { inferOrgAdditionalFields, organizationClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import {
  convexClient,
  crossDomainClient,
} from "@convex-dev/better-auth/client/plugins";

import { guardIdentifyUrl, sentinelClient } from "./guard/client";
import { studioAc, studioRoles } from "./studio-access";

// Echoes Guard project that fingerprints visitors (identify). Without the
// public id the Sentinel client is left out and nothing is sent to Guard.
const guardPublicId = import.meta.env.VITE_GUARD_PUBLIC_ID;
const guardKvUrl = import.meta.env.VITE_GUARD_KV_URL;

// Fingerprints the visitor and auto-solves Sentinel's proof-of-work
// challenges on sign-in/sign-up. It only adds request hooks. The cast is for
// the types alone: the plugin is built against a newer @better-fetch/fetch
// than our better-auth (1.6.11) ships, and the two copies' hook types don't
// line up. It can go once better-auth is on 1.6.30, like Guard.
const sentinel = guardPublicId
  ? [
      sentinelClient({
        identifyUrl: guardIdentifyUrl(guardPublicId, guardKvUrl),
        autoSolveChallenge: true,
      }) as BetterAuthClientPlugin,
    ]
  : [];

export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_CONVEX_SITE_URL,
  plugins: [
    convexClient(),
    crossDomainClient(),
    // Companies: design studios and brands (src/lib/organizations.ts). The
    // fields mirror convex/auth.ts; only `kind` is given by the client.
    organizationClient({
      // Studios' roles (src/lib/studio-access.ts), their own included.
      ac: studioAc,
      roles: studioRoles,
      dynamicAccessControl: { enabled: true },
      schema: inferOrgAdditionalFields({
        organization: {
          additionalFields: {
            kind: { type: "string", required: true, input: true },
            status: { type: "string", required: true, input: false },
            rejectedBecause: { type: "string", required: false, input: false },
            website: { type: "string", required: false, input: true },
          },
        },
      }),
    }),
    ...sentinel,
  ],
});
